import {useMutation} from '@tanstack/react-query';
import {
  Control,
  FormState,
  UseFormClearErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormResetField,
  UseFormReturn,
  UseFormSetError,
  UseFormSetFocus,
  UseFormSetValue,
  UseFormTrigger,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import {toast} from '@ui/toast/toast';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import {User} from '@ui/types/user';
import {message} from '@ui/i18n/message';
import {apiClient, queryClient} from '@common/http/query-client';
import {DatatableDataQueryKey} from '@common/datatable/requests/paginated-resources';
import {HostAcc} from '@app/titles/models/HostAcc';

interface Payload {
  expire_at?: undefined | string | Date;
  username?: string;
  api: string;
  provider: number | string;
  active?: boolean;
  id?: number;
}

export function useAddHosters(form: {
  watch: UseFormWatch<HostAcc> | UseFormWatch<any>;
  getValues: UseFormGetValues<HostAcc> | UseFormGetValues<any>;
  getFieldState: UseFormGetFieldState<HostAcc> | UseFormGetFieldState<any>;
  setError: UseFormSetError<HostAcc> | UseFormSetError<any>;
  clearErrors: UseFormClearErrors<HostAcc> | UseFormClearErrors<any>;
  setValue: UseFormSetValue<HostAcc> | UseFormSetValue<any>;
  trigger: UseFormTrigger<HostAcc> | UseFormTrigger<any>;
  formState: FormState<HostAcc> | FormState<any>;
  resetField: UseFormResetField<HostAcc> | UseFormResetField<any>;
  reset: UseFormReset<HostAcc> | UseFormReset<any>;
  handleSubmit:
    | UseFormHandleSubmit<HostAcc, undefined>
    | UseFormHandleSubmit<any, undefined>;
  unregister: UseFormUnregister<HostAcc> | UseFormUnregister<any>;
  control: Control<HostAcc, any> | Control<any, any>;
  register: UseFormRegister<HostAcc> | UseFormRegister<any>;
  setFocus: UseFormSetFocus<HostAcc> | UseFormSetFocus<any>;
}) {
  return useMutation({
    mutationFn: (props: Payload) => updateAccountDetails(props),
    onSuccess: async () => {
      toast(message('Host added successfully'));
      await queryClient.invalidateQueries({
        queryKey: DatatableDataQueryKey('debridors'),
      });
    },
    onError: r => onFormQueryError(r, form),
  });
}

async function updateAccountDetails(payload: Payload): Promise<any> {
  const provider = parseInt(payload.provider as any);
  if (provider === 2) {
    const endpoint =
      'https://api.alldebrid.com/v4/user?agent=webapp_v4&apikey=' + payload.api;
    const resutl = await fetch(endpoint).then(r => r.json());
    if (resutl.status == 'success' && resutl.data.user) {
      payload.username = resutl.data.user.username;
      if (resutl.data.user.isPremium) {
        const timestamp = resutl.data.user.premiumUntil;
        const date = new Date(timestamp * 1000); // Convertir le timestamp en millisecondes
        payload.expire_at = date.toISOString().slice(0, 19).replace('T', ' ');
      }
      return apiClient.post(`debridors`, payload).then(r => r.data);
    } else {
      if (resutl.status == 'error') {
        toast.danger(message(resutl.error.message));
        throw new Error(resutl.error.message);
      } else {
        toast.danger(message('An error occured'));
        throw new Error('An error occured');
      }
    }
  } else {
    return apiClient.post(`debridors`, payload).then(r => r.data);
  }
}
